import { BaseCol } from '@app/components/common/BaseCol/BaseCol';
import { BaseRow } from '@app/components/common/BaseRow/BaseRow';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { CustomerInfoForm } from '@app/components/forms/CustomerInfoForm/CustomerInfoForm';
import { TabCard } from '@app/components/layouts/TabCard/TabCard';
import StatusBar from '@app/components/statusBar/statusBar';
import { useHeader } from '@app/components/header/layouts/HeaderContext';
import { Form } from 'antd';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { notificationController } from '@app/controllers/notificationController';
import useApiErrorHandler from '@app/components/Error/ErrorMsgHandling';
import PaymentsInfor from '../Labs&Research/PaymentsInfor';
import TaskInfo from './TaskInfoForm';
import LabItems from './LabItems/LabItems';
import ImportItems from './ImportItems/ImportItems';
import ExportItems from './ExportItems/ExportItems';
import FollowUpItems from './FollowUpItems/FollowUpItems';
import {
  createNominalPayment,
  editNominalPayment,
  showNominalPayment,
  storeNominalTask,
  updateNominalTask,
} from '@app/api/nominalPayments.api';
import { NominalPaymenAPIData } from '@app/models/nominal_payments/NominalPaymentResponse';

interface NominalPaymentProps {
  id?: string;
  type: 'edit' | 'create' | 'view';
  pageTitle: string;
  editable: boolean;
  setCurrentBreadcrumName: (name: string) => void;
}

export const NominalPaymentsComponent: React.FC<NominalPaymentProps> = ({ id, type, setCurrentBreadcrumName }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { addHeaderButton, removeHeaderButton } = useHeader();
  const [form] = Form.useForm();

  const location = useLocation();
  const defaultTab = location.state?.activeTab || '1';
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [nompay_code, setNomPayCode] = useState('New');
  const [status, setStatus] = useState<string | null>(null);
  const [isLoading, setLoading] = useState(false);
  const [departmentOptions, setDepartmentOptions] = useState<any[]>([]);
  const [divisionOptions, setDivisionOptions] = useState<any[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  const [currentType, setCurrentType] = useState<'edit' | 'create' | 'view'>(type);
  const [wfActions, setWfActions] = useState<string[]>([]);
  const isEditable = currentType === 'create' || currentType === 'edit';
  const npId = id ? parseInt(id) : null;
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [invoiceData, setInvoiceData] = useState<any[]>([]);
  const handleErrorMessages = useApiErrorHandler();
  const [total, setTotal] = useState<string | number | null>(null);

  const [formData, setFormData] = useState<{
    TaskInfoForm: any;
    customerInfo: any;
    LabItems: {
      tests: any[];
      products: any[];
      vaccines: any[];
    };
    ImportItems: {
      products: any[];
      service_charges: any[];
    };
    FollowUpItems: {
      products: any[];
      service_charges: any[];
    };
    ExportItems: {
      products: any[];
      service_charges: any[];
    };
  }>({
    TaskInfoForm: {},
    customerInfo: {},
    LabItems: {
      tests: [],
      products: [],
      vaccines: [],
    },
    ImportItems: {
      products: [],
      service_charges: [],
    },
    FollowUpItems: {
      products: [],
      service_charges: [],
    },
    ExportItems: {
      products: [],
      service_charges: [],
    },
  });
  const formDataRef = useRef(formData);

  const forms = {
    customerInfo: Form.useForm()[0],
    TaskInfoForm: Form.useForm()[0],
  };

  // console.log('Type:', type);
  // console.log('Current Type:', currentType);
  // console.log('Current status:', status);

  useEffect(() => {
    const isDraftOrNew = !status || status === 'DRAFT';
    const showEditBtn = status === 'DRAFT' || status === undefined;

    const buttons: Record<string, React.ReactElement | false> = {
      'save-button': isEditable && isDraftOrNew && (
        <BaseButton type="primary" loading={isLoading} size="small" onClick={handleSave}>
          Save
        </BaseButton>
      ),
      'cancel-button': isEditable && isDraftOrNew && (
        <BaseButton onClick={() => navigate(-1)} size="small" style={{ marginRight: '8px' }}>
          {t('Cancel')}
        </BaseButton>
      ),
      'edit-button': currentType === 'view' && showEditBtn && (
        <BaseButton type="primary" loading={isLoading} size="small" onClick={() => setCurrentType('edit')}>
          Edit
        </BaseButton>
      ),
    };

    Object.entries(buttons).forEach(([key, button]) => button && addHeaderButton(key, button));

    return () => {
      // Remove specific buttons that were added
      Object.keys(buttons).forEach((key) => {
        if (buttons[key as keyof typeof buttons]) {
          removeHeaderButton(key);
        }
      });
    };
  }, [currentType, isEditable, addHeaderButton, removeHeaderButton, isLoading, navigate, status, t]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const apiResponse =
          npId && currentType === 'view'
            ? await showNominalPayment(npId)
            : npId && currentType === 'edit'
            ? await editNominalPayment(npId)
            : currentType === 'create'
            ? await createNominalPayment()
            : null;

        if (!(apiResponse && typeof apiResponse === 'object')) {
          console.warn('Invalid API response format');
          return;
        }

        const res = apiResponse as NominalPaymenAPIData;
        console.log('API Response:', res);

        res.department_options?.length && setDepartmentOptions(res.department_options);
        res.lab_division_options?.length && setDivisionOptions(res.lab_division_options);
        console.log('Setting wfActions:', res?.wf_actions);
        setWfActions(res?.wf_actions ?? []);
        setInvoiceData(res?.nominal_payment?.invoices ?? []);
        setTotal(res?.nominal_payment?.invoices?.[0]?.total ?? null);

        console.log('set total:', total);

        const nompay = res.nominal_payment;
        if (nompay) {
          setStatus(nompay.status);
          setNomPayCode(nompay.code || 'New');
          setCurrentBreadcrumName(nompay.code || 'New');
          setUploadedFiles(nompay.documents || []);

          const nominalPaymentData = res.nominal_payment;

          if (nominalPaymentData) {
            const labItemsData = {
              tests: nominalPaymentData.lab_services?.tests || [],
              vaccines: nominalPaymentData.lab_services?.vaccines || [],
              products: nominalPaymentData.lab_services?.products || [],
            };

            const importItemsData = {
              products: nominalPaymentData.imports?.products || [],
              service_charges: nominalPaymentData.imports?.service_charges || [],
            };

            const exportItemsData = {
              products: nominalPaymentData.exports?.products || [],
              service_charges: nominalPaymentData.exports?.service_charges || [],
            };

            const followUpItemsData = {
              products: nominalPaymentData.follow_ups?.products || [],
              service_charges: nominalPaymentData.follow_ups?.service_charges || [],
            };

            setFormData((prevFormData) => ({
              ...prevFormData,
              LabItems: labItemsData,
              ImportItems: importItemsData,
              ExportItems: exportItemsData,
              FollowUpItems: followUpItemsData,
            }));

            // Update the ref to keep it in sync
            formDataRef.current = {
              ...formDataRef.current,
              LabItems: labItemsData,
              ImportItems: importItemsData,
              ExportItems: exportItemsData,
              FollowUpItems: followUpItemsData,
            };

            console.log('Distributed Data:', {
              LabItems: labItemsData,
              ImportItems: importItemsData,
              ExportItems: exportItemsData,
              FollowUpItems: followUpItemsData,
            });

            const customerInfoData = {
              id: nompay.customer.id || '',
              name: nompay.customer.name || '',
              customer_type: nompay.customer.customer_type,
              email: nompay.customer.user?.email || '',
              status: nompay.customer.user?.status || '',
              address: nompay.customer.address || '',
              contact_no: nompay.customer.contact_no || '',
              nic: nompay.customer.nic || '',
              business_name: nompay.additional_info?.business_name || '',
              business_address: nompay.additional_info?.business_address || '',
              company_type: nompay.additional_info?.company_type || '',
              business_registration_no: nompay.additional_info?.business_reg_no || '',
              department_id: nompay.customer.id?.toString() || '',
              department_code: nompay.customer.code || '',
              department_name: nompay.customer.name || '',
              department_address: nompay.customer.address || '',
              department_description: nompay.customer.description || '',
              referenceable_label: nompay.customer.referenceable_label || null,
            };
            forms.customerInfo.setFieldsValue(customerInfoData);
          }

          const taskInfoData = {
            description: nompay.description || '',
            department: nompay.department_id || '',
            // date: nompay.date || new Date().toISOString().split('T')[0],
          };
          forms.TaskInfoForm.setFieldsValue(taskInfoData);
        } else {
          // Handle create mode - set empty data structure
          const emptyFormData = {
            TaskInfoForm: {},
            customerInfo: {},
            LabItems: {
              tests: [],
              products: [],
              vaccines: [],
            },
            ImportItems: {
              products: [],
              service_charges: [],
            },
            FollowUpItems: {
              products: [],
              service_charges: [],
            },
            ExportItems: {
              products: [],
              service_charges: [],
            },
          };

          setFormData(emptyFormData);
          formDataRef.current = emptyFormData;
        }
      } catch (error) {
        console.error('Error fetching lab data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentType, status]);

  const handleLabItemsChange = (items: { tests: any[]; products: any[]; vaccines: any[] }) => {
    const updated = {
      ...formDataRef.current,
      LabItems: items,
    };
    formDataRef.current = updated;
    setFormData(updated);
  };

  const handleImportItemsChange = (items: { products: any[]; service_charges: any[] }) => {
    const updated = {
      ...formDataRef.current,
      ImportItems: items,
    };
    formDataRef.current = updated;
    setFormData(updated);
  };

  const handleFollowUpItemsChange = (items: { products: any[]; service_charges: any[] }) => {
    const updated = {
      ...formDataRef.current,
      FollowUpItems: items,
    };
    formDataRef.current = updated;
    setFormData(updated);
  };

  const handleExportItemsChange = (items: { products: any[]; service_charges: any[] }) => {
    const updated = {
      ...formDataRef.current,
      ExportItems: items,
    };
    formDataRef.current = updated;
    setFormData(updated);
  };

  const handleSave = async () => {
    try {
      const customerInfoValues = await forms.customerInfo.validateFields();

      let description;
      let requestDepartment;
      const date = new Date().toISOString().split('T')[0];
      try {
        const TaskInfoValues = await forms.TaskInfoForm.validateFields();
        description = TaskInfoValues.description;
        requestDepartment = TaskInfoValues.department;
      } catch (validationError) {
        const allTaskFormValues = forms.TaskInfoForm.getFieldsValue();
        description = allTaskFormValues.description || formData.TaskInfoForm?.description || '';
      }

      const payload = {
        customer_id: customerInfoValues.id,
        department_id: requestDepartment,
        description: description,
        date: date,
        lab_services: formDataRef.current.LabItems,
        imports: formDataRef.current.ImportItems,
        follow_ups: formDataRef.current.FollowUpItems,
        exports: formDataRef.current.ExportItems,
      };

      console.log('Submitting data:', payload);

      setLoading(true);

      const action = type === 'create' ? storeNominalTask(payload) : npId ? updateNominalTask(npId, payload) : null;

      action
        ?.then((res: any) => {
          setLoading(false);
          notificationController.success({ message: res.message });
          navigate(`/view-nominal-payment`, { state: { id: res?.data?.id ?? npId } });
          setCurrentType('view');
        })
        .catch((err: any) => {
          console.error('API Error:', err);
          handleErrorMessages(err);
          notificationController.error({ message: err.message || 'Failed to save data' });
          setLoading(false);
        });
    } catch (error) {
      console.error('Validation error:', error);
      setLoading(false);
    }
  };

  const tabs = useMemo(
    () => [
      {
        key: '1',
        title: t('Customer Info'),
        content: (
          <CustomerInfoForm
            form={forms.customerInfo}
            type={currentType}
            createType={'NOMPAY'}
            customerInfo={formData.customerInfo}
            departmentOptions={departmentOptions}
          />
        ),
      },
      {
        key: '2',
        title: t('Task Info'),
        content: (
          <TaskInfo
            form={forms.TaskInfoForm}
            type={currentType}
            departmentOptions={departmentOptions}
            nompayId={npId}
            documents={uploadedFiles}
            status={status}
          />
        ),
      },
      {
        key: '3',
        title: t('Lab Items'),
        content: (
          <LabItems
            onItemsChange={handleLabItemsChange}
            divisionOptions={divisionOptions}
            labItemsData={formData.LabItems}
            editable={isEditable}
            type={currentType}
          />
        ),
      },
      {
        key: '4',
        title: t('Import Items'),
        content: (
          <ImportItems
            onItemsChange={handleImportItemsChange}
            departmentOptions={departmentOptions}
            importItemsData={formData.ImportItems}
            editable={isEditable}
            type={currentType}
          />
        ),
      },
      {
        key: '5',
        title: t('Follow Up Items'),
        content: (
          <FollowUpItems
            onItemsChange={handleFollowUpItemsChange}
            departmentOptions={departmentOptions}
            followUpItemsData={formData.FollowUpItems}
            editable={isEditable}
            type={currentType}
          />
        ),
      },
      {
        key: '6',
        title: t('Export Items'),
        content: (
          <ExportItems
            onItemsChange={handleExportItemsChange}
            departmentOptions={departmentOptions}
            exportItemsData={formData.ExportItems}
            editable={isEditable}
            type={currentType}
          />
        ),
      },
      ...(status !== null && status !== 'DRAFT'
        ? [
            {
              key: '7',
              title: t('Payments'),
              content: (
                <PaymentsInfor
                  id={id ?? null}
                  invoice_data={invoiceData}
                  lab_code={nompay_code ?? null}
                  labStatus={status}
                  wfActions={wfActions || []}
                  invoicetype={'nominal_payment'}
                  total={total}
                />
              ),
            },
          ]
        : []),
    ],
    [currentType, isEditable, status, departmentOptions, formData],
  );

  return (
    <BaseRow gutter={[16, 16]}>
      <BaseCol span={24}>
        <StatusBar
          id={npId ?? null}
          status={status}
          setStatus={setStatus}
          wfActions={wfActions || []}
          currentType={currentType}
          workflowType="np"
          onViewInvoicesClick={() => setActiveTab('7')}
        />
      </BaseCol>
      <BaseCol span={24}>
        <TabCard tabs={tabs} activeKey={activeTab} onChange={setActiveTab} title={nompay_code} />
      </BaseCol>
    </BaseRow>
  );
};
